import React from 'react'

import { Box, Colors, EnhancedDimension, Text } from '@factorialco/deprecated-design-system'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { Answer as AnswerType } from 'modules/performance_process/components/insights/common/presenters'

interface AnswerCellProps {
  answer?: AnswerType
  withRichTextEditor: boolean
  onClick?: () => void
}

interface TextAnswerDisplayProps {
  text: string
  isComment?: boolean
  withRichTextEditor: boolean
}

const TextAnswerDisplay = ({
  text,
  isComment = false,
  withRichTextEditor,
}: TextAnswerDisplayProps) => {
  if (withRichTextEditor) {
    return (
      <Box width="full" height={isComment ? 's96' : 's120'} overflowY="auto" paddingRight="s12">
        <RichTextDisplay content={text} />
      </Box>
    )
  }

  return (
    <Text.Normal size="200" lineClamp={5}>
      {text}
    </Text.Normal>
  )
}

export const AnswerCell: React.FC<AnswerCellProps> = React.memo(
  ({ answer, withRichTextEditor, onClick }) => (
    <Box.Motion
      width="full"
      alignItems="flexStart"
      height="full"
      justifyContent="flexStart"
      borderRadius={{ all: 'abs008' }}
      border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
      padding="s16"
      paddingRight="s4"
      cursor="pointer"
      background="white"
      gap="s8"
      onClick={onClick}
    >
      {answer?.type == null ? (
        <>
          {answer == null && (
            <Text.Normal size="200" lineClamp={5}>
              -
            </Text.Normal>
          )}
          {answer?.answerText && answer.answerText.length > 0 && (
            <TextAnswerDisplay text={answer.answerText} withRichTextEditor={withRichTextEditor} />
          )}
          {answer?.answerInt && (
            <Text.Normal size="200" lineClamp={5}>
              {answer.answerInt}
            </Text.Normal>
          )}
          {answer?.answerChoice &&
            answer.answerChoice.map((choice) => (
              <Text.Normal size="200" lineClamp={5} key={choice}>
                {choice}
              </Text.Normal>
            ))}
        </>
      ) : (
        <>
          {answer?.score != null && (
            <Text.Normal size="200" weight="semibold">
              {answer.score} - {answer.scoreLabel?.toUpperCase()}
            </Text.Normal>
          )}
          {answer?.comment ? (
            <TextAnswerDisplay
              text={answer.comment}
              withRichTextEditor={withRichTextEditor}
              isComment
            />
          ) : (
            <Text.Normal size="200" color="grey600">
              {i18n.t('performance_process.insights.answers.no_comment_provided')}
            </Text.Normal>
          )}
        </>
      )}
    </Box.Motion>
  )
)

AnswerCell.displayName = 'AnswerCell'

const scoreColors: Record<
  NonNullable<AnswerType['type']>,
  {
    text: Colors
    background: Colors
  }
> = {
  low: { text: 'radicalRed1500', background: 'radicalRed200' },
  normal: { text: 'viridian1200', background: 'viridian100' },
  high: { text: 'viridian1200', background: 'viridian400' },
}

interface ScoreAnswerCellProps {
  answer?: AnswerType
  height?: EnhancedDimension
  onClick?: () => void
}

export const ScoreAnswerCell: React.FC<ScoreAnswerCellProps> = React.memo(
  ({ answer, height = 's48', onClick }) => {
    return (
      <Box.Motion
        width="full"
        background={answer?.type ? scoreColors[answer?.type].background : 'lighter000'}
        height={height}
        alignItems="flexEnd"
        justifyContent="flexEnd"
        borderRadius={{ all: 'abs008' }}
        paddingX="s16"
        paddingY="s8"
        whileHover={{ opacity: 0.8 }}
        whileTap={{ scale: 0.98 }}
        style={{ userSelect: 'none' }}
        cursor={onClick ? 'pointer' : 'default'}
        onClick={onClick}
      >
        {answer?.type && (
          <Text.Normal weight="bold" color={scoreColors[answer?.type].text}>
            {answer?.score}
          </Text.Normal>
        )}
      </Box.Motion>
    )
  }
)

ScoreAnswerCell.displayName = 'ScoreAnswerCell'
